import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import isNil from 'lodash/isNil';
import * as API from 'api';
import { AccordionSection, BankReconciliationTable, ButtonExportCSVAsync, CardContent, CardIcon, CardLabel, CardValue, DateRangePicker, Empty, SectionHeader, TableSection } from 'components';
import InfiniteScroll from 'components/InfiniteScroll/InfiniteScroll';
import { appConfig } from 'config/app';
import { theme } from 'design';
import { getQueryStringValue, useQueryString } from 'hooks';
import { getPrefundLedgerError } from 'store/Collections';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { ApiQueryOperator, BankReconciliationReportType, IApiList, ILedgerEntry, LedgerEntryType } from 'types';
import { DEFAULT_OFFSET, getDailyLedgerEntries } from 'utils';
import { getLastElement, sumBy } from 'utils/arrays';
import { CSVFormatter, IExportCSV } from 'utils/csvformatter';
import { centsToDollarsDisplay } from 'utils/currency';
import { formatDateForApi } from 'utils/date';
import { BRCard, BRMultiTableLayout, BRSection, BRSummaryRow } from './components';

const DEFAULT_START_DATE = formatDateForApi(dayjs().subtract(appConfig.DATE_RANGE.DEFAULT_DAYS, 'days'));
const DEFAULT_END_DATE = formatDateForApi(dayjs());
const LIST_OFFSET = 10;

const ReportsBankReconciliation = () => {
  const dispatch = useDispatch();
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const [isLoading, setIsLoading] = useState(true);
  const [creditsOffset, setCreditsOffset] = useState(LIST_OFFSET);
  const [debitsOffset, setDebitsOffset] = useState(LIST_OFFSET);
  const [ledger, setLedger] = useState<IApiList<ILedgerEntry> | null>(null);
  const ledgerEntries = ledger?.data || [];

  const [urlQuery, setUrlQuery] = useQueryString('reportsLedger', window.location, {
    limit: 0,
    offset: DEFAULT_OFFSET,
    sort: 'created_at',
    q: [
      { key: 'payment_date', operator: ApiQueryOperator.gte, value: DEFAULT_START_DATE },
      { key: 'payment_date', operator: ApiQueryOperator.lte, value: DEFAULT_END_DATE },
    ],
  });

  const selectedStartDate = getQueryStringValue(urlQuery.q, 'payment_date', ApiQueryOperator.gte) as string;
  const selectedEndDate = getQueryStringValue(urlQuery.q, 'payment_date', ApiQueryOperator.lte) as string;

  useEffect(() => {
    if (currentSite?.id && selectedStartDate && selectedEndDate) {
      setIsLoading(true);
      API.Sites.listPrefundLedgerEntries(currentSite.id, selectedStartDate, selectedEndDate, { limit: 0 })
        .then((response) => setLedger(response.body))
        .catch((error) => dispatch(getPrefundLedgerError(error)))
        .finally(() => setIsLoading(false));
    }
  }, [currentSite?.id, selectedStartDate, selectedEndDate, dispatch]);

  const { credits, debits } = useMemo(
    () =>
      ledgerEntries.reduce(
        (acc, entry) => {
          if (entry.entryType === LedgerEntryType.CREDIT) {
            acc.credits.push(entry);
          } else if (entry.entryType === LedgerEntryType.DEBIT) {
            acc.debits.push(entry);
          }
          return acc;
        },
        { credits: [], debits: [] }
      ),
    [ledgerEntries]
  );
  const displayedCredits = credits.slice(0, creditsOffset);
  const displayedDebits = debits.slice(0, debitsOffset);

  const loadMoreCredits = useCallback(async () => {
    setCreditsOffset((currentOffset) => {
      if (credits.length > currentOffset) {
        return currentOffset + LIST_OFFSET;
      }
      return currentOffset;
    });
  }, [credits]);

  const loadMoreDebits = useCallback(async () => {
    setDebitsOffset((currentOffset) => {
      if (debits.length > currentOffset) {
        return currentOffset + LIST_OFFSET;
      }
      return currentOffset;
    });
  }, [debits]);

  // calculations for Summary
  const { startingBalance, totalCredits, totalDebits, endingBalance } = useMemo(() => {
    const firstEntry = ledgerEntries?.[0];
    let startBalance = firstEntry?.balance || 0;

    // need to credit or debit the amount from the first entry to get the true start balance
    if (firstEntry) {
      if (firstEntry.entryType === LedgerEntryType.CREDIT) {
        startBalance -= firstEntry.amount;
      } else if (firstEntry.entryType === LedgerEntryType.DEBIT) {
        startBalance += firstEntry.amount;
      }
    }

    return {
      startingBalance: startBalance,
      totalCredits: sumBy(credits, 'amount'),
      totalDebits: sumBy(debits, 'amount'),
      endingBalance: getLastElement(ledgerEntries)?.balance || 0,
    };
  }, [ledgerEntries, credits, debits]);

  // calculations for Daily Reports section
  const { dailyCredits, dailyDebits } = useMemo(() => ({ dailyCredits: getDailyLedgerEntries(credits), dailyDebits: getDailyLedgerEntries(debits) }), [credits, debits]);

  const hasDailyReportData = !!dailyCredits && !!dailyDebits && (dailyCredits.length >= 1 || dailyDebits.length >= 1);
  const hasDetailsReportsData = !!credits && !!debits && (credits.length >= 1 || debits.length >= 1);

  const onDateRangeChange = ([start, end]: string[]) => {
    const isValid = dayjs(start, 'YYYY-MM-DD', true).isValid() && dayjs(end, 'YYYY-MM-DD', true).isValid();
    if (isValid) {
      // Set local filters
      const filters = [
        { key: 'payment_date', operator: ApiQueryOperator.gte, value: start },
        { key: 'payment_date', operator: ApiQueryOperator.lte, value: end },
      ];

      // Clear ledger
      setLedger(null);
      setUrlQuery({ q: filters, offset: 0 });
      setCreditsOffset(LIST_OFFSET);
      setDebitsOffset(LIST_OFFSET);
    }
  };

  const exportSummaryCSV = async (startingBalance: number, endingBalance: number, totalCredits: number, totalDebits: number): Promise<IExportCSV> => CSVFormatter.bankRecSummary(startingBalance, endingBalance, totalCredits, totalDebits);
  const exportDailyCSV = async (credits: { date: string; amount: number }[], debits: { date: string; amount: number }[]): Promise<IExportCSV> => CSVFormatter.bankRecDaily(credits, debits);
  const exportDetailsCSV = async (credits: ILedgerEntry[], debits: ILedgerEntry[]): Promise<IExportCSV> => CSVFormatter.bankRecDetails(credits, debits);

  return (
    currentSite && (
      <>
        <BRSection>
          <SectionHeader title="Bank Reconciliation" subtitle="Choose a date range to run report">
            <DateRangePicker
              fluidWidth={false}
              inputHeight="small"
              onSubmit={onDateRangeChange}
              pickerPosition="right"
              startDate={selectedStartDate}
              endDate={selectedEndDate}
              submitLabel="apply"
              maxDays={appConfig.DATE_RANGE.MAX_DAYS_3_MONTHS}
              setGlobalDateRange={false}
            />
          </SectionHeader>
        </BRSection>

        <AccordionSection
          dataCy="summary-section"
          actions={
            <ButtonExportCSVAsync
              data-cy="summary-export-btn"
              disabled={isLoading}
              exportFunc={() => exportSummaryCSV(startingBalance, endingBalance, totalCredits, totalDebits)}
              fileName={`BankSummary_${currentSite.name}_${selectedStartDate}_${selectedEndDate}`}
            />
          }
          hideContentOverflow
          title="Summary"
        >
          <BRSummaryRow>
            <BRCard>
              <CardIcon bgColor={theme.color.neutral[100]} icon="Wallet" fill={theme.color.neutral[400]} />
              <CardContent data-cy="summary-starting-balance-card">
                <CardLabel isLoading={isLoading}>Starting Balance</CardLabel>
                <CardValue isLoading={isLoading}>{!isNil(startingBalance) ? centsToDollarsDisplay(startingBalance) : '--'}</CardValue>
              </CardContent>
            </BRCard>

            <BRCard>
              <CardIcon bgColor={theme.color.success[25]} icon="Payment" fill={theme.color.success[50]} />
              <CardContent data-cy="summary-credits-card">
                <CardLabel isLoading={isLoading}>Credits</CardLabel>
                <CardValue isLoading={isLoading}>{!isNil(totalCredits) ? centsToDollarsDisplay(totalCredits as number) : '--'}</CardValue>
              </CardContent>
            </BRCard>

            <BRCard>
              <CardIcon bgColor={theme.color.warning[25]} icon="Payment" fill={theme.color.warning[50]} />
              <CardContent data-cy="summary-debits-card">
                <CardLabel isLoading={isLoading}>Debits</CardLabel>
                <CardValue isLoading={isLoading}>{!isNil(totalDebits) ? `(${centsToDollarsDisplay(totalDebits as number)})` : '--'}</CardValue>
              </CardContent>
            </BRCard>

            <BRCard>
              <CardIcon bgColor={theme.color.danger[25]} icon="Payment" fill={theme.color.danger[50]} />
              <CardContent data-cy="summary-ending-balance-card">
                <CardLabel isLoading={isLoading}>Ending Balance</CardLabel>
                <CardValue isLoading={isLoading}>{!isNil(endingBalance) ? centsToDollarsDisplay(endingBalance as number) : '--'}</CardValue>
              </CardContent>
            </BRCard>
          </BRSummaryRow>
        </AccordionSection>

        <AccordionSection
          dataCy="daily-section"
          actions={
            <ButtonExportCSVAsync
              data-cy="daily-reports-export-csv-btn"
              disabled={!hasDailyReportData}
              exportFunc={() => exportDailyCSV(dailyCredits, dailyDebits)}
              fileName={`BankDaily_${currentSite.name}_${selectedStartDate}_${selectedEndDate}`}
            />
          }
          hideContentOverflow
          title="Daily Reports"
        >
          <BRMultiTableLayout>
            <TableSection headerText="Daily Credits" headerCount={dailyCredits.length} tooltipText="These are credits to your Kickfin account." tooltipId="daily-credits-header-tooltip" isLoading={isLoading} dataCy="daily-credits">
              {!isLoading && dailyCredits?.length === 0 ? (
                <Empty container containerBorder containerHeight={66} message="No credits found for this date range." />
              ) : (
                <BankReconciliationTable tableData={dailyCredits} isLoading={isLoading} reportType={BankReconciliationReportType.DAILY_CREDITS} dataCy="daily-credits-report" />
              )}
            </TableSection>

            <TableSection headerText="Daily Debits" headerCount={dailyDebits.length} tooltipText="These are debits to your Kickfin account." tooltipId="daily-debits-header-tooltip" isLoading={isLoading} dataCy="daily-debits">
              {!isLoading && dailyDebits?.length === 0 ? (
                <Empty container containerBorder containerHeight={66} message="No debits found for this date range." />
              ) : (
                <BankReconciliationTable tableData={dailyDebits} isLoading={isLoading} reportType={BankReconciliationReportType.DAILY_DEBITS} dataCy="daily-debits-report" />
              )}
            </TableSection>
          </BRMultiTableLayout>
        </AccordionSection>

        <AccordionSection
          dataCy="detailed-section"
          actions={
            <ButtonExportCSVAsync
              data-cy="detailed-report-export-btn"
              disabled={!hasDetailsReportsData}
              exportFunc={() => exportDetailsCSV(credits, debits)}
              fileName={`BankDetails_${currentSite.name}_${selectedStartDate}_${selectedEndDate}`}
            />
          }
          hideContentOverflow
          title="Detailed Reports"
        >
          <BRMultiTableLayout>
            <TableSection headerText="Credit Details" headerCount={credits.length} tooltipText="These are credits to your Kickfin account." tooltipId="credit-details-header-tooltip" isLoading={isLoading} dataCy="credit-details">
              {!isLoading && credits?.length === 0 ? (
                <Empty container containerBorder containerHeight={66} message="No credits found for this date range." />
              ) : (
                <InfiniteScroll id="credits" fetchMore={loadMoreCredits}>
                  <BankReconciliationTable tableData={displayedCredits} isLoading={isLoading} reportType={BankReconciliationReportType.DETAILED_CREDITS} dataCy="credit-details-report" />
                </InfiniteScroll>
              )}
            </TableSection>

            <TableSection headerText="Debit Details" headerCount={debits.length} tooltipText="These are debits to your Kickfin account." tooltipId="debit-details-header-tooltip" isLoading={isLoading} dataCy="debit-details">
              {!isLoading && debits?.length === 0 ? (
                <Empty container containerBorder containerHeight={66} message="No debits found for this date range" />
              ) : (
                <InfiniteScroll id="debits" fetchMore={loadMoreDebits}>
                  <BankReconciliationTable tableData={displayedDebits} isLoading={isLoading} reportType={BankReconciliationReportType.DETAILED_DEBITS} dataCy="debit-details-report" />
                </InfiniteScroll>
              )}
            </TableSection>
          </BRMultiTableLayout>
        </AccordionSection>
      </>
    )
  );
};

export default ReportsBankReconciliation;
